/**
 * Financial Calculations — ROI, NPV, and Break Even computations for adjacency nodes.
 *
 * All calculations operate on individual AdjacencyNode financial fields:
 * - iR&D (internal R&D) + fR&D (funded R&D) = total investment
 * - estRevenue = estimated annual revenue
 */

import type { AdjacencyNode } from '../types/adjacency';

export interface FinancialMetrics {
  /** Total investment = iR&D + fR&D (dollars). */
  totalInvestment: number;
  /** Net investment = iR&D only (IWT's cost, since fR&D is external funding). */
  netIwtInvestment: number;
  /** ROI % = (Est Revenue - Total Investment) / Total Investment × 100. Null if no data. */
  roi: number | null;
  /** Break Even years = Total Investment / Est Annual Revenue. Null if no data. */
  breakEvenYears: number | null;
  /** NPV at given discount rate and period. Null if no data. */
  npv: number | null;
}

/**
 * Compute financial metrics for a single node.
 */
export function computeFinancialMetrics(
  node: AdjacencyNode,
  discountRate: number,
  periodYears: number,
): FinancialMetrics {
  const iRnD = node.iRnD ?? 0;
  const fRnD = node.fRnD ?? 0;
  const totalInvestment = iRnD + fRnD;
  const netIwtInvestment = iRnD;
  const estRevenue = node.estRevenue;

  let roi: number | null = null;
  let breakEvenYears: number | null = null;
  let npv: number | null = null;

  if (estRevenue != null && estRevenue > 0 && totalInvestment > 0) {
    roi = ((estRevenue - totalInvestment) / totalInvestment) * 100;
    breakEvenYears = totalInvestment / estRevenue;
    npv = computeNpv(totalInvestment, estRevenue, discountRate, periodYears);
  }

  return { totalInvestment, netIwtInvestment, roi, breakEvenYears, npv };
}

/**
 * NPV = -Investment + Σ(Revenue / (1 + r)^t) for t=1 to periodYears.
 * Assumes constant annual revenue.
 */
export function computeNpv(
  investment: number,
  annualRevenue: number,
  discountRate: number,
  periodYears: number,
): number {
  let pv = 0;
  for (let t = 1; t <= periodYears; t++) {
    pv += annualRevenue / Math.pow(1 + discountRate, t);
  }
  return pv - investment;
}

/** Format a number as USD currency (no decimals). */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

/** Format a number as compact currency ($1.2M, $350K). */
export function formatCompactCurrency(value: number): string {
  if (Math.abs(value) >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1)}M`;
  }
  if (Math.abs(value) >= 1_000) {
    return `$${(value / 1_000).toFixed(0)}K`;
  }
  return formatCurrency(value);
}
